import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';

/** Persisted / user-chosen mode. `system` follows the OS (`prefers-color-scheme`). */
export const THEME_MODES = ['system', 'light', 'dark'] as const;
export type ThemeMode = (typeof THEME_MODES)[number];

/** What is actually painted: `system` resolves to one of these via `matchMedia`. */
export type ResolvedTheme = 'light' | 'dark';

/** `localStorage` key; the no-flash inline script in `index.html` reads the same key. */
export const THEME_STORAGE_KEY = 'servicetim.theme';

function isThemeMode(value: string | null): value is ThemeMode {
  return value !== null && (THEME_MODES as readonly string[]).includes(value);
}

function prefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false;
}

function resolve(mode: ThemeMode): ResolvedTheme {
  return mode === 'system' ? (prefersDark() ? 'dark' : 'light') : mode;
}

/** Applies (or removes) `.dark` on `<html>` and sets `color-scheme` so native controls match. */
function paint(resolved: ResolvedTheme): void {
  const root = document.documentElement;
  // Swap without animating: components carry `transition-colors`, and a staggered fade between
  // palettes looks broken (and lets contrast checks sample half-way colours). Both apps define
  // `.theme-switching * { transition: none }`; the class is dropped once the new palette painted.
  if (root.classList.contains('dark') !== (resolved === 'dark')) {
    root.classList.add('theme-switching');
    window.requestAnimationFrame(() =>
      window.requestAnimationFrame(() => root.classList.remove('theme-switching')),
    );
  }
  root.classList.toggle('dark', resolved === 'dark');
  root.style.colorScheme = resolved;
}

function readStoredMode(): ThemeMode {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemeMode(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
}

export interface ThemeContextValue {
  /** The user's chosen mode (`system`, `light` or `dark`). */
  mode: ThemeMode;
  /** What is actually applied right now (`system` resolved against the OS). */
  resolvedTheme: ResolvedTheme;
  setMode: (mode: ThemeMode) => void;
  /** Cycles `light -> dark -> system -> light …` (used by a simple icon-only toggle). */
  cycleMode: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * Colour-mode provider (ADR-0022): mounts once per app. Mirrors the no-flash inline script in
 * `index.html` (same storage key, same `system` resolution) so there is no flash between the
 * script's guess and React's first render, and reacts to OS changes while `mode === 'system'`.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() =>
    typeof window === 'undefined' ? 'system' : readStoredMode(),
  );
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    typeof window === 'undefined' ? 'light' : resolve(mode),
  );

  useEffect(() => {
    const next = resolve(mode);
    setResolvedTheme(next);
    paint(next);
  }, [mode]);

  useEffect(() => {
    if (mode !== 'system' || !window.matchMedia) return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      const next = resolve('system');
      setResolvedTheme(next);
      paint(next);
    };
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [mode]);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Private browsing / storage disabled: the mode still applies for this load.
    }
  }, []);

  const cycleMode = useCallback(() => {
    const order: ThemeMode[] = ['light', 'dark', 'system'];
    setMode(order[(order.indexOf(mode) + 1) % order.length] ?? 'system');
  }, [mode, setMode]);

  return (
    <ThemeContext.Provider value={{ mode, resolvedTheme, setMode, cycleMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme() must be called inside <ThemeProvider>');
  return ctx;
}
